/**
 * The platform entities that triggers watch, and the changes to them.
 *
 * A platform event is about one subject: a file, a dataset, an invocation, or
 * an event (the annotation). The catalog states that subject
 * per type, so the picker can ask "which entity?" and then "which changes?"
 * without ever needing a noun for the platform event itself. A trigger
 * subscribed to one entity is also guaranteed a common subject: every change
 * to a file carries the file and its dataset, so conditions, templates, and
 * "fire once per" all have something to bind to.
 */

import {
  EVENT_CATALOG,
  eventLabel,
  legalOncePerFor,
  SUBSCRIBABLE_EVENT_TYPES,
  type SubjectType,
} from "./eventsCatalog";
import { OncePer, PlatformEventType } from "./TriggerV2Record";

/** A subject a trigger may watch: every subject type some platform event is about. */
export type PlatformEntity = SubjectType;

/** The entity a platform event is about: `file.uploaded` → `file`. */
export function entityOf(eventType: PlatformEventType): PlatformEntity {
  return EVENT_CATALOG[eventType].subjectType;
}

/** The entities with subscribable changes, in catalog order. */
export const PLATFORM_ENTITIES: readonly PlatformEntity[] =
  SUBSCRIBABLE_EVENT_TYPES.map(entityOf).filter(
    (entity, index, all) => all.indexOf(entity) === index,
  );

/** Picker label: "File". */
export function entityLabel(entity: PlatformEntity): string {
  return entity.charAt(0).toUpperCase() + entity.slice(1);
}

/** With its article, for prose: "a file", "an invocation". */
export function entityArticle(entity: PlatformEntity): string {
  return `${/^[aeiou]/.test(entity) ? "an" : "a"} ${entity}`;
}

/** The subscribable changes to one entity, in catalog order. */
export function changesTo(entity: PlatformEntity): PlatformEventType[] {
  return SUBSCRIBABLE_EVENT_TYPES.filter(
    (eventType) => entityOf(eventType) === entity,
  );
}

/**
 * How an action fired by changes to this entity gets its inputs, or `null`
 * when some change carries a dataset for the action to run against. Changes
 * that carry no dataset root hand the action no files, so it selects its
 * inputs by query.
 */
export function subjectNote(entity: PlatformEntity): string | null {
  if (
    changesTo(entity).some((e) => EVENT_CATALOG[e].roots.includes("dataset"))
  ) {
    return null;
  }
  return `Changes to ${entityArticle(entity)} carry no data of their own, so actions must select their inputs by query.`;
}

/**
 * The one entity a subscription watches, or `null` when it is empty or
 * spans several. The editor offers one entity per trigger; a subscription
 * that mixes them (authored through the SDK) is shown but not edited.
 */
export function entityOfSubscription(
  events: readonly PlatformEventType[],
): PlatformEntity | null {
  if (events.length === 0) {
    return null;
  }
  const first = entityOf(events[0]);
  return events.every((eventType) => entityOf(eventType) === first)
    ? first
    : null;
}

export interface OncePerChoice {
  value: OncePer;
  label: string;
  /** What choosing it means, in one sentence. */
  description: string;
}

/** The grain that means "once per this entity": the entity's own name. */
function ownGrainOf(entity: PlatformEntity): OncePer | null {
  return Object.values(OncePer).find((grain) => grain === entity) ?? null;
}

/**
 * The change half of an event type, as words (`file.metadata_updated` →
 * "metadata updated"), for prose that has already named the entity. A label
 * standing on its own uses `eventLabel`, which leads with the entity.
 */
function changeLabel(eventType: PlatformEventType): string {
  return eventType.slice(eventType.indexOf(".") + 1).replace(/_/g, " ");
}

/**
 * What each grain means, written out per change.
 *
 * Hand-written rather than templated. The sentences are only nearly regular --
 * "Uploading it again" beside "Later changes to it", "the first upload into a
 * dataset" beside "the first ingestion in a dataset" -- so a template would
 * need an exception per change and still could not be read without running it.
 * Templating this is what produced "A repeated uploaded for the same file".
 *
 * A change missing from here, and every subscription of more than one change,
 * falls back to `genericDescription`.
 */
const ONCE_PER_COPY: Partial<
  Record<PlatformEventType, Partial<Record<OncePer, string>>>
> = {
  [PlatformEventType.FileUploaded]: {
    [OncePer.File]: "Fires once per file. Uploading it again does not fire it.",
    [OncePer.Dataset]:
      "Fires on the first upload into a dataset. Later uploads do not.",
    [OncePer.Occurrence]:
      "Fires on every upload, even when the same file is uploaded again.",
  },
  [PlatformEventType.FileIngested]: {
    [OncePer.File]: "Fires once per file. Ingesting it again does not fire it.",
    [OncePer.Dataset]:
      "Fires on the first ingestion in a dataset. Later ingestions do not.",
    [OncePer.Occurrence]:
      "Fires on every ingestion, even when the same file is ingested again.",
  },
  [PlatformEventType.FileMetadataUpdated]: {
    [OncePer.File]: "Fires once per file. Later changes to it do not fire it.",
    [OncePer.Dataset]:
      "Fires on the first metadata change in a dataset. Later changes do not.",
    [OncePer.Occurrence]:
      "Fires on every metadata change, even repeated changes to one file.",
  },
  [PlatformEventType.DatasetCreated]: {
    [OncePer.Dataset]:
      "Fires once per dataset. A second creation event for it does not fire it.",
    [OncePer.Occurrence]:
      "Fires on every creation, even a repeat for the same dataset.",
  },
  [PlatformEventType.UploadCompleted]: {
    [OncePer.Dataset]:
      "Fires on the first completed upload into a dataset. Later uploads do not.",
    [OncePer.Occurrence]:
      "Fires on every completed upload, even repeated uploads into one dataset.",
  },
  [PlatformEventType.DatasetMetadataUpdated]: {
    [OncePer.Dataset]:
      "Fires once per dataset. Later changes to it do not fire it.",
    [OncePer.Occurrence]:
      "Fires on every metadata change, even repeated changes to one dataset.",
  },
  [PlatformEventType.DatasetTagAdded]: {
    [OncePer.Occurrence]:
      "Fires each time a tag is added, including further tags on one dataset.",
  },
  [PlatformEventType.InvocationCompleted]: {
    [OncePer.Invocation]:
      "Fires once per invocation. A later completion of it does not fire it.",
    [OncePer.Occurrence]:
      "Fires on every completion, even a repeat for the same invocation.",
  },
  [PlatformEventType.InvocationFailed]: {
    [OncePer.Invocation]:
      "Fires once per invocation. A retry that fails again does not fire it.",
    [OncePer.Occurrence]:
      "Fires on every failure, including repeated failures of one invocation.",
  },
};

/**
 * What a grain means with no one change to name: either the trigger watches
 * several at once, or the one it watches has no wording of its own. The
 * sentence says "change" and leaves which one open.
 */
function genericDescription(
  grain: OncePer,
  own: OncePer | null,
  noun: string,
): string {
  if (grain === own) {
    return `Fires once per ${noun}. Any changes do not fire it again.`;
  }
  if (grain === OncePer.Occurrence) {
    return `Fires on every change, even repeated changes to one ${noun}.`;
  }
  return `Fires on the first change in a ${grain}. Later changes do not.`;
}

/**
 * The `once_per` choices legal for a subscription, ordered from the entity
 * itself outward: the entity, then its container, then every occurrence.
 *
 * One change is explained in its own words, from `ONCE_PER_COPY`. Several
 * changes, or one with no wording there, are explained in terms of the entity
 * being watched.
 */
export function oncePerChoices(
  events: readonly PlatformEventType[],
): OncePerChoice[] {
  if (events.length === 0) {
    return [];
  }
  const entity = entityOfSubscription(events);
  const own = entity === null ? null : ownGrainOf(entity);
  const noun = entity ?? "subject";
  const ordered = [...legalOncePerFor(events)].sort(
    (a, b) => rank(a, own) - rank(b, own),
  );
  // One change speaks for itself; several have no one change to name.
  const written = events.length === 1 ? ONCE_PER_COPY[events[0]] : undefined;
  return ordered.map((grain) => ({
    value: grain,
    label: labelFor(grain, own, entity),
    description: written?.[grain] ?? genericDescription(grain, own, noun),
  }));
}

/** Names the grain only; the description says which change repeats. */
function labelFor(
  grain: OncePer,
  own: OncePer | null,
  entity: PlatformEntity | null,
): string {
  if (grain === OncePer.Occurrence) {
    return "Every time";
  }
  return `Once per ${entityLabel(grain === own && entity !== null ? entity : grain)}`;
}

function rank(grain: OncePer, own: OncePer | null): number {
  if (grain === own) {
    return 0;
  }
  if (grain === OncePer.Occurrence) {
    return 2;
  }
  return 1;
}

/**
 * The grain a fresh subscription starts with: the first choice offered, which
 * is the entity's own grain when its changes allow it, else the nearest legal
 * one. Every platform event supports `occurrence`, so there is always an
 * answer for a non-empty subscription.
 */
export function defaultOncePerFor(
  events: readonly PlatformEventType[],
): OncePer {
  return oncePerChoices(events)[0]?.value ?? OncePer.Occurrence;
}

/**
 * Whether an action target gets just the changed file: the runtime makes
 * the file the unit of work at every grain finer than its dataset, so a
 * file trigger firing once per file or on every occurrence mounts that file
 * alone, and only the dataset grain runs on the whole dataset.
 */
export function actionTakesTheFile(
  events: readonly PlatformEventType[],
  oncePer: OncePer,
): boolean {
  return entityOfSubscription(events) === "file" && oncePer !== OncePer.Dataset;
}

/** "File · uploaded, ingested": the subscription as the picker shows it. */
export function readableSubscription(
  events: readonly PlatformEventType[],
): string {
  const entity = entityOfSubscription(events);
  if (entity === null) {
    return events.map(eventLabel).join(", ");
  }
  return `${entityLabel(entity)} · ${events.map(changeLabel).join(", ")}`;
}
